import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Add,
  ArrowBack,
  Close,
  Delete,
  Edit,
  Psychology,
  School,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { api } from '../../../../services/Main/Api'
import MarkdownEditor from '../../../../components/Ticket/MarkdownEditor'
import Markdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import moment from 'moment'

const CATEGORIES = [
  { value: 'GERAL', label: 'Geral', color: '#64748B' },
  { value: 'DIMOB', label: 'DIMOB', color: '#7C3AED' },
  { value: 'CONTRATOS', label: 'Contratos', color: '#0073ea' },
  { value: 'FATURAS', label: 'Faturas', color: '#D97706' },
  { value: 'REPASSES', label: 'Repasses', color: '#16A34A' },
  { value: 'IMOVEIS', label: 'Imóveis', color: '#EA580C' },
  { value: 'BANCO', label: 'Banco', color: '#DC2626' },
]

const getCategoryColor = (value) => CATEGORIES.find(c => c.value === value)?.color || '#64748B'

const AIKnowledgeBase = () => {
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  // Form state
  const [title, setTitle] = useState('')
  const [category, setCategory] = useState('GERAL')
  const [content, setContent] = useState('')
  const [saving, setSaving] = useState(false)

  const fetchItems = () => {
    setLoading(true)
    api.get('tickets/knowledge/')
      .then(res => setItems(res.data.results || res.data))
      .catch(console.error)
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchItems() }, [])

  const resetForm = () => {
    setTitle('')
    setCategory('GERAL')
    setContent('')
    setEditingItem(null)
  }

  const openCreate = () => {
    resetForm()
    setDialogOpen(true)
  }

  const openEdit = (item) => {
    setTitle(item.title)
    setCategory(item.category)
    setContent(item.content)
    setEditingItem(item)
    setDialogOpen(true)
  }

  const handleSave = () => {
    if (!title.trim() || !content.trim()) return
    setSaving(true)

    const data = { title, category, content }
    const request = editingItem
      ? api.put(`tickets/knowledge/${editingItem.id}/`, data)
      : api.post('tickets/knowledge/', data)

    request
      .then(() => {
        setDialogOpen(false)
        resetForm()
        fetchItems()
      })
      .catch(console.error)
      .finally(() => setSaving(false))
  }

  const handleDelete = (id) => {
    api.delete(`tickets/knowledge/${id}/`)
      .then(() => {
        setDeleteConfirm(null)
        fetchItems()
      })
      .catch(console.error)
  }

  const handleToggleActive = (item) => {
    api.patch(`tickets/knowledge/${item.id}/`, { is_active: !item.is_active })
      .then(() => fetchItems())
      .catch(console.error)
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton onClick={() => navigate('/admin/tickets')} size="small">
            <ArrowBack />
          </IconButton>
          <Psychology sx={{ fontSize: 28, color: '#7C3AED' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Base de Conhecimento da IA
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ensine a IA sobre as regras de negócio da plataforma
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreate}
          sx={{ bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' }, textTransform: 'none', fontWeight: 600 }}
        >
          Novo Conhecimento
        </Button>
      </Box>

      {/* Info card */}
      <Card sx={{ mb: 3, bgcolor: '#F5F3FF', border: '1px solid #DDD6FE' }}>
        <CardContent sx={{ py: '12px !important', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <School sx={{ color: '#7C3AED', fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: '#5B21B6' }}>
            As regras ativas são automaticamente incluídas no contexto da IA quando ela investiga um ticket.
            Quanto mais específica a regra, melhor será a investigação.
          </Typography>
        </CardContent>
      </Card>

      {/* Items list */}
      <Box display="flex" flexDirection="column" gap={2}>
        {loading ? (
          [...Array(3)].map((_, i) => (
            <Card key={i}><CardContent><Skeleton variant="text" width="40%" /><Skeleton variant="text" /><Skeleton variant="text" width="60%" /></CardContent></Card>
          ))
        ) : items.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Psychology sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                Nenhum conhecimento cadastrado ainda
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Adicione regras de negócio para a IA investigar tickets com mais precisão
              </Typography>
            </CardContent>
          </Card>
        ) : (
          items.map(item => (
            <Card
              key={item.id}
              sx={{
                border: '1px solid',
                borderColor: item.is_active ? '#E2E8F0' : '#FEE2E2',
                opacity: item.is_active ? 1 : 0.6,
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                  <Box display="flex" alignItems="center" gap={1} flex={1}>
                    <Chip
                      label={CATEGORIES.find(c => c.value === item.category)?.label || item.category}
                      size="small"
                      sx={{
                        bgcolor: getCategoryColor(item.category) + '15',
                        color: getCategoryColor(item.category),
                        fontWeight: 600,
                        fontSize: 11,
                      }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {item.title}
                    </Typography>
                    {!item.is_active && (
                      <Chip label="Inativo" size="small" sx={{ bgcolor: '#FEE2E2', color: '#991B1B', fontSize: 10, height: 20 }} />
                    )}
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title={item.is_active ? 'Desativar' : 'Ativar'}>
                      <Switch
                        size="small"
                        checked={item.is_active}
                        onChange={() => handleToggleActive(item)}
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#7C3AED' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#7C3AED' } }}
                      />
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => openEdit(item)}>
                        <Edit sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton size="small" onClick={() => setDeleteConfirm(item.id)} sx={{ color: '#DC2626' }}>
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box sx={{ pl: 0.5, '& p': { margin: 0, fontSize: 14, color: '#475569', lineHeight: 1.6 }, '& ul, & ol': { margin: '4px 0', paddingLeft: '20px' } }}>
                  <Markdown remarkPlugins={[remarkBreaks]}>{item.content}</Markdown>
                </Box>

                <Box display="flex" gap={2} mt={1.5}>
                  <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                    Criado por {item.created_by_name || 'Sistema'} · {moment(item.created_at).fromNow()}
                  </Typography>
                  {item.updated_at !== item.created_at && (
                    <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                      Atualizado {moment(item.updated_at).fromNow()}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {editingItem ? 'Editar Conhecimento' : 'Novo Conhecimento'}
            </Typography>
            <IconButton size="small" onClick={() => setDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          <Box display="flex" gap={2} mb={2.5}>
            <TextField
              fullWidth
              label="Título"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: DIMOB é sempre do ano anterior"
              size="small"
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Categoria</InputLabel>
              <Select value={category} label="Categoria" onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map(c => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569', mb: 1 }}>
            Conteúdo
          </Typography>
          <MarkdownEditor
            value={content}
            onChange={setContent}
            placeholder="Descreva a regra de negócio ou informação que a IA deve considerar..."
            minHeight={150}
          />

          <Divider sx={{ my: 2.5 }} />

          <Box display="flex" justifyContent="flex-end" gap={1.5}>
            <Button
              onClick={() => setDialogOpen(false)}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !title.trim() || !content.trim()}
              sx={{ bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' }, textTransform: 'none', fontWeight: 600 }}
            >
              {saving ? 'Salvando...' : editingItem ? 'Salvar alterações' : 'Criar conhecimento'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Excluir conhecimento?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Essa ação não pode ser desfeita. A IA deixará de considerar esta regra nas investigações.
          </Typography>
          <Box display="flex" gap={1.5} justifyContent="center">
            <Button onClick={() => setDeleteConfirm(null)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={() => handleDelete(deleteConfirm)}
              sx={{ bgcolor: '#DC2626', '&:hover': { bgcolor: '#B91C1C' }, textTransform: 'none' }}
            >
              Excluir
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default AIKnowledgeBase
